'use client';

import { AnimatePresence, motion } from 'motion/react';
import { GraphicLottie } from '@/app/components/questionnaireV2/components/survey-item/components/interstitial/interstitial-shared';

interface LoadingBarItem {
  id: string;
  label: string;
  duration: number;
  delay: number;
}

const loadingBars: LoadingBarItem[] = [
  {
    id: 'bar-1',
    label: 'Calculating your lifetime Risk Score',
    duration: 3,
    delay: 0,
  },
  {
    id: 'bar-2',
    label: 'Generating your personalized Action Plan',
    duration: 3,
    delay: 1,
  },
  {
    id: 'bar-3',
    label: 'Developing your annual Testing Plan',
    duration: 3,
    delay: 2,
  },
  {
    id: 'bar-4',
    label: 'Finalizing updates',
    duration: 3,
    delay: 3,
  },
];

const headerImage = 'risk_grid_small.json';

/**
 * Minimal version of CalculatingCancerRisk for health data recalculation.
 * Shows 4 loading bars with descriptive text and the animated risk grid header.
 */
export const RecalculatingCancerRisk = () => {

  return (
    <div className="dashboard-reset relative flex h-full w-full items-center justify-center">
      <div className="absolute left-1/2 top-[100px] flex h-[100px] max-w-[530px] -translate-x-1/2 overflow-hidden">
        <div className="flex -mt-[15px] mb-[50px]">
          <GraphicLottie srcUrl={headerImage} loop={true} className="h-[115px] w-[177px]" />
          <GraphicLottie srcUrl={headerImage} loop={true} className="h-[115px] w-[177px]" />
          <GraphicLottie srcUrl={headerImage} loop={true} className="h-[115px] w-[177px]" />
        </div>
      </div>

      <div className="flex w-full justify-center px-8">
        <div className="flex w-full max-w-[307px] flex-col items-center">
          <div className="mb-10 w-full text-center">
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: 'easeInOut' }}
              className="mb-2 text-2xl font-bold text-white"
            >
              We are analyzing your results
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2, ease: 'easeInOut' }}
              className="text-base font-normal text-white opacity-90"
            >
              Please do not close this browser window or press the back button
            </motion.p>
          </div>

          {/* Loading bars */}
          <div className="w-full max-w-[264px] space-y-[30px]">
            <AnimatePresence>
              {loadingBars.map((item) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.4,
                    delay: item.delay,
                    ease: 'easeOut',
                  }}
                  className="flex w-full flex-col gap-2"
                >
                  {/* Progress bar */}
                  <div className="h-4 w-full overflow-hidden rounded-full bg-[#151a20]">
                    <motion.div
                      initial={{ width: '0%' }}
                      animate={{
                        width: '100%',
                      }}
                      transition={{
                        duration: item.duration,
                        delay: item.delay,
                        ease: 'easeOut',
                      }}
                      className="h-full rounded-full"
                      style={{
                        background:
                          'linear-gradient(to right, #7cf1c7 0%, #ff9473 164.58%)',
                        willChange: 'transform',
                      }}
                    />
                  </div>

                  {/* Label */}
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      duration: 0.3,
                      delay: item.delay + 0.2,
                    }}
                    className="text-sm font-normal leading-[1.4] text-white"
                  >
                    {item.label}
                  </motion.p>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};
